// Adaptive Learn & Earn — the engine (LB-19, docs/ADAPTIVE_LE_SCOPE.md §2, §4, §5).
//
// Pure functions over a per-child, per-game state object. Nothing here reads React, KV or
// the network; the wiring in ChildView / App calls draw() at session start and record() at
// session finish. When Adaptive is OFF for the household or the game is on a manual level,
// none of this is called and the existing draws run exactly as before (§9).
//
// Vocabulary: items carry `grade` (1–5 inside the band) and `topic` (the category). A child
// has, per game, a `target` grade, a rolling `window` of recent results, per-`topic` mastery,
// and a `band` — the WORKING band the app owns (§2). The child's schoolYear is the parent's
// number and is never touched.
//
// Parameters come from the CMS config entry `adaptive.params` (cms-seed/adaptive-params.json)
// via adaptiveParams(); the defaults below are the launch values Alan signed off on 10 Sep.

export const ADAPTIVE_GAMES = ["spelling", "vocab", "mathsworkout", "ttstreaks"];

export const DEFAULT_PARAMS = {
  UP: 85, DOWN: 50, K: 3, N: 15, MASTER: 90, M: 10, W: 1, P: 80,
  draw: { at: 60, below: 20, above: 20 }, retention: 10,
  // Band-down (Maths Ladder v2, Alan 22 Sep): the grade a child restarts at in the band
  // below — the middle, so a move down is "easier questions", not "back to the very start".
  DOWN_TARGET: 3,
};

// Merge the CMS config entry over the defaults; tolerate a missing or partial entry.
export function adaptiveParams(configValue) {
  const c = configValue && typeof configValue === "object" ? configValue : {};
  return {
    ...DEFAULT_PARAMS, ...c,
    draw: { ...DEFAULT_PARAMS.draw, ...(c.draw && typeof c.draw === "object" ? c.draw : {}) },
  };
}

export function emptyGameState(band) {
  return { band: band || null, target: 1, window: [], sessions: [], topics: {}, topAt: null, bottomAt: null, lastSessionDay: null, movedUpAt: null, movedDownAt: null, pinned: false };
}

export function gameState(adaptive, game, band) {
  const s = adaptive && adaptive[game] ? adaptive[game] : null;
  if (s) { if (!s.band && band) s.band = band; return s; }
  return emptyGameState(band);
}

const clampGrade = (g) => Math.max(1, Math.min(5, Math.round(g) || 1));
const gradeOf = (item) => clampGrade(Number.isInteger(item.grade) ? item.grade : 3);

function shuffle(arr) { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }

function isMastered(state, topic) { const t = state.topics[topic]; return !!(t && t.mastered); }

// ---- draw ------------------------------------------------------------------------------
// items: [{ id?, word|..., grade, topic }]. Returns `size` items: ~draw.at% at target,
// draw.below% one below, draw.above% one above; mastered topics stay in at `retention`% as
// retention checks (never vanish, §4); recently missed items ("practice") return first.
// Falls back to neighbouring grades when a grade has nothing left, so a thin band still
// fills the session.
export function adaptiveDraw(items, state, params, size) {
  const P = adaptiveParams(params);
  const pool = items.filter(Boolean);
  if (!pool.length || size <= 0) return [];
  const target = clampGrade(state.target);
  const practice = new Set(state.practice || []);
  const keyOf = (it) => String(it.id ?? it.word ?? it.key);
  const live = pool.filter((it) => !isMastered(state, it.topic));
  const masteredItems = pool.filter((it) => isMastered(state, it.topic));
  const byGrade = (g) => shuffle(live.filter((it) => gradeOf(it) === g));
  const want = {
    practice: Math.min(Math.ceil(size * 0.2), practice.size),
    retention: masteredItems.length ? Math.round((size * P.retention) / 100) : 0,
  };
  const out = [];
  const taken = new Set();
  const take = (arr, n) => { for (const it of arr) { if (out.length >= size || n <= 0) break; const k = keyOf(it); if (taken.has(k)) continue; taken.add(k); out.push(it); n--; } };
  take(shuffle(live.filter((it) => practice.has(keyOf(it)))), want.practice);
  take(shuffle(masteredItems), want.retention);
  const remaining = size - out.length;
  const nAbove = Math.round((remaining * P.draw.above) / 100);
  const nBelow = Math.round((remaining * P.draw.below) / 100);
  const nAt = remaining - nAbove - nBelow;
  take(byGrade(target), nAt);
  if (target < 5) take(byGrade(target + 1), nAbove); else take(byGrade(target), nAbove);
  if (target > 1) take(byGrade(target - 1), nBelow); else take(byGrade(target), nBelow);
  // fill from the nearest grades outward, then anything
  for (let d = 1; out.length < size && d <= 4; d++) { take(byGrade(target - d), size - out.length); take(byGrade(target + d), size - out.length); }
  if (out.length < size) take(shuffle(pool), size - out.length);
  return shuffle(out);
}

// ---- record ----------------------------------------------------------------------------
// results: [{ id?, word?, grade, topic, correct: bool }]. day: "YYYY-MM-DD". kind: "session"
// (counts) | "daily" | "shop" (never counted, §4). abandoned: true for an exit before the end.
// Returns { state, events: [{ type: "target_up"|"target_down"|"topic_mastered"|"band_up"|"band_down", ... }] }.
export function adaptiveRecord(stateIn, results, { day, kind = "session", abandoned = false, bandOrder = [], topicsInBand = [] } = {}, params) {
  const P = adaptiveParams(params);
  const state = JSON.parse(JSON.stringify(stateIn));
  const events = [];
  if (kind !== "session") return { state, events };
  const keyOf = (r) => String(r.id ?? r.word ?? r.key);

  // practice set: missed items come back sooner (§4, LB-23 Tricky Ones)
  const practice = new Set(state.practice || []);
  for (const r of results) { if (r.correct) practice.delete(keyOf(r)); else practice.add(keyOf(r)); }
  state.practice = [...practice].slice(-40);

  // per-topic mastery (only items at or above target count toward mastery of a topic)
  for (const r of results) {
    if (!r.topic) continue;
    // A levelled item (maths, tables) counts toward mastery of its skill only at level 3 —
    // small numbers right is not the skill mastered (Alan, 2d).
    if (Number.isInteger(r.level) && r.level < 3) continue;
    const t = state.topics[r.topic] || (state.topics[r.topic] = { seen: 0, correct: 0, recent: [], mastered: false, masteredAt: null });
    t.seen++; if (r.correct) t.correct++;
    t.recent = [...t.recent, r.correct ? 1 : 0].slice(-P.M);
    const acc = t.recent.length >= P.M ? (100 * t.recent.reduce((a, b) => a + b, 0)) / t.recent.length : 0;
    if (!t.mastered && acc >= P.MASTER) { t.mastered = true; t.masteredAt = day; events.push({ type: "topic_mastered", topic: r.topic }); }
    else if (t.mastered && !r.correct && acc < P.MASTER - 20) { t.mastered = false; } // retention miss → back to practice; the badge is kept by the UI
  }

  // rolling window of results at or above target → session accuracy
  const target = clampGrade(state.target);
  const counted = results.filter((r) => gradeOf(r) >= target);
  state.window = [...state.window, ...counted.map((r) => (r.correct ? 1 : 0))].slice(-P.N);
  const acc = counted.length ? (100 * counted.filter((r) => r.correct).length) / counted.length : null;
  // Zip B: a persistent per-test log for the parent surfaces (tests last week, the score
  // trend, average score). sessions[] resets on every target/band move by design, so it
  // cannot answer "how many tests last week" — this log never resets, only rolls off.
  {
    const score = results.filter((r) => r.correct).length;
    state.log = [...(state.log || []), { day, score, total: results.length }].slice(-12);
  }
  // one session per day counts toward K (three tests in one sitting are one signal)
  if (state.lastSessionDay !== day) {
    state.sessions = [...state.sessions, { day, acc, abandoned }].slice(-P.K * 2);
    state.lastSessionDay = day;
  } else if (state.sessions.length) {
    const last = state.sessions[state.sessions.length - 1];
    if (acc !== null) last.acc = last.acc === null ? acc : Math.max(last.acc, acc);
    if (abandoned) last.abandoned = true;
  }
  const lastK = state.sessions.slice(-P.K);
  let weakNow = false;
  if (lastK.length >= P.K) {
    const allUp = lastK.every((s) => s.acc !== null && s.acc >= P.UP && !s.abandoned);
    const allDown = lastK.every((s) => (s.acc !== null && s.acc <= P.DOWN) || s.abandoned);
    weakNow = allDown;
    if (allUp && target < 5) { state.target = target + 1; state.sessions = []; state.window = []; events.push({ type: "target_up", target: state.target }); }
    else if (allDown && target > 1) { state.target = target - 1; state.sessions = []; state.window = []; events.push({ type: "target_down", target: state.target }); }
    else if (allUp && target === 5 && !state.topAt) { state.topAt = day; }
    // Band-down (Maths Ladder v2): K weak sessions with nowhere lower to go inside the band
    // start the clock, the mirror of topAt.
    else if (allDown && target === 1 && !state.bottomAt) { state.bottomAt = day; }
  }
  if (state.target === 5 && !state.topAt) state.topAt = day;
  if (state.target < 5) state.topAt = null;
  if (state.target > 1) state.bottomAt = null;

  // Level 2 — the band moves up when the target has sat at the top for W weeks AND P% of the
  // band's topics are mastered. Never when pinned. The working band is the app's; the
  // parent's schoolYear is untouched (§2).
  if (!state.pinned && state.band && state.target === 5 && state.topAt && topicsInBand.length) {
    const weeks = (Date.parse(day) - Date.parse(state.topAt)) / (7 * 86400000);
    const masteredCount = topicsInBand.filter((t) => isMastered(state, t)).length;
    const pct = (100 * masteredCount) / topicsInBand.length;
    const idx = bandOrder.indexOf(state.band);
    if (weeks >= P.W && pct >= P.P && idx >= 0 && idx < bandOrder.length - 1) {
      const from = state.band;
      state.band = bandOrder[idx + 1];
      state.target = 1; state.window = []; state.sessions = []; state.topAt = null; state.movedUpAt = day;
      state.history = [...(state.history || []), { day, from, to: state.band }].slice(-12);
      events.push({ type: "band_up", from, to: state.band });
    }
  }
  // Level 2, downward (Maths Ladder v2 — Alan, 22 Sep: "yes to band down/band up"). The mirror
  // of the move up: the target has sat at 1 for W weeks since K weak sessions first put it
  // there, AND the latest K sessions are still weak at the moment of the move (a child who
  // has recovered to middling scores is not moved). One step down the band order, restarting
  // at DOWN_TARGET so they land in the middle of the easier band. Never when pinned, never
  // below the first band. Until this existed a band was sticky downward: a child placed a
  // rung too high by the ladder could only be rescued by a parent override — which also
  // switched the Coach off for that game.
  if (!state.pinned && state.band && state.target === 1 && state.bottomAt && weakNow) {
    const weeks = (Date.parse(day) - Date.parse(state.bottomAt)) / (7 * 86400000);
    const idx = bandOrder.indexOf(state.band);
    if (weeks >= P.W && idx > 0) {
      const from = state.band;
      state.band = bandOrder[idx - 1];
      state.target = clampGrade(P.DOWN_TARGET); state.window = []; state.sessions = []; state.bottomAt = null; state.topAt = null; state.movedDownAt = day;
      state.history = [...(state.history || []), { day, from, to: state.band }].slice(-12);
      events.push({ type: "band_down", from, to: state.band });
    }
  }
  return { state, events };
}

// ---- reads for the surfaces -------------------------------------------------------------
export function adaptiveSummary(state, topicsInBand = []) {
  const s = state || emptyGameState();
  const mastered = Object.entries(s.topics).filter(([, t]) => t.mastered).map(([k]) => k);
  const wordsMastered = Object.values(s.topics).reduce((n, t) => n + (t.mastered ? t.seen : 0), 0);
  return { target: clampGrade(s.target), band: s.band, pinned: !!s.pinned, movedUpAt: s.movedUpAt || null, movedDownAt: s.movedDownAt || null, topicsMastered: mastered.length, topicsInBand: topicsInBand.length, wordsMasteredApprox: wordsMastered, trendUp: !!s.movedUpAt || (s.sessions || []).length === 0 && s.target > 1 };
}

// Maths / tables: a question type's grade is its position in the band (§3.5), derived here
// so nothing needs seeding.
export function gradeByPosition(orderedIds) {
  const n = orderedIds.length; const out = {};
  orderedIds.forEach((id, i) => { out[id] = n <= 1 ? 3 : Math.min(5, 1 + Math.floor((i * 5) / n)); });
  return out;
}
export const TABLE_GRADE = { 2: 1, 5: 1, 10: 1, 3: 2, 4: 2, 6: 3, 9: 3, 7: 4, 8: 4, 11: 5, 12: 5, 1: 1 };

// ---- fortnightly line (§8) — feed card on parent Home; only ever "mastered" / "moved up".
// Returns [] when there is nothing to say (a fortnight with nothing produces no line).
export function fortnightHighlights(profiles, learningContent, todayStr, labels = {}) {
  const since = Date.parse(todayStr) - 14 * 86400000;
  const out = [];
  for (const child of profiles || []) {
    const adaptive = (learningContent || {})[child.id]?.adaptive || {};
    for (const [game, st] of Object.entries(adaptive)) {
      if (!st) continue;
      const gname = { spelling: "spelling", vocab: "vocabulary", mathsworkout: "maths", ttstreaks: "times tables" }[game] || game;
      if (st.movedUpAt && Date.parse(st.movedUpAt) >= since) out.push({ childId: child.id, name: child.name, game, kind: "band_up", text: `${child.name} moved up to ${labels[st.band] || st.band} in ${gname}` });
      const mastered = Object.values(st.topics || {}).filter((t) => t.mastered && t.masteredAt && Date.parse(t.masteredAt) >= since).length;
      if (mastered) out.push({ childId: child.id, name: child.name, game, kind: "mastered", text: `${child.name} mastered ${mastered} ${gname} topic${mastered === 1 ? "" : "s"}` });
    }
  }
  return out;
}
// One sentence per game for the "How Adaptive is helping" panel (§8), from totals only.
export function adaptiveSentence(game, st, labels = {}) {
  if (!st) return null;
  const gname = { spelling: "Spelling", vocab: "Vocabulary", mathsworkout: "Maths", ttstreaks: "Times tables" }[game] || game;
  const mastered = Object.values(st.topics || {}).filter((t) => t.mastered).length;
  const band = labels[st.band] || st.band || "";
  const parts = [];
  if (band) parts.push(`working in ${band}`);
  parts.push(`grade ${Math.max(1, Math.min(5, st.target || 1))} of 5`);
  if (mastered) parts.push(`${mastered} topic${mastered === 1 ? "" : "s"} mastered`);
  if (st.movedUpAt) parts.push(`moved up ${st.movedUpAt}`);
  if (st.pinned) parts.push("pinned");
  return { game: gname, text: parts.join(" · "), history: (st.history || []).map((h) => h.day) };
}

// ---- starting target (3c) ----
// Inside a school-year band the blend starts at grade 1 — the band already fits the year.
// Tables have no band, so the year sets the start: a Year 2 child begins on 2, 5, 10; a
// Year 4+ child on the 6s, 9s, 7s and 8s. Alan, 12 Sep: "a 9 year old being given the 2 times
// table is dumb." Import kept local to avoid a cycle: resolveSchoolYear lives in age.js.
import { resolveSchoolYear } from "./age.js";
export function startingTarget(game, child) {
  if (game !== "ttstreaks") return 1;
  const y = resolveSchoolYear(child);
  const n = typeof y === "string" ? parseInt(y.replace(/\D/g, ""), 10) : Number(y);
  if (!Number.isFinite(n)) return 1;
  return n <= 2 ? 1 : n === 3 ? 2 : n === 4 ? 3 : n === 5 ? 4 : 5;
}

// ---- kid-facing level words (Alan, 10 Sep): the target grade inside a band, as a stage
// word, never a number; checked against every band name for collisions ("Apprentice" lost
// to "Master Readers · Apprentice").
export const LEVEL_WORDS = ["Starter", "Rising", "Skilled", "Expert", "Champion"];
export function levelWord(target) { return LEVEL_WORDS[Math.max(1, Math.min(5, Math.round(target || 1))) - 1]; }

// ---- weekly summary (Alan, 10 Sep: weekly, not fortnightly; a dismissable card on parent
// Home and a push; only when the child did something). One line per child from the last
// seven days of engine state: sessions per game, words/topics mastered, and a focus area
// (the topic that keeps coming back). Shared by the Home card (src) and the Monday push
// (worker/lib/notifications.js), so the two never say different things.
export function weeklySummaryFor(child, learning, todayStr, gameNames = { spelling: "spelling", vocab: "vocabulary", mathsworkout: "maths", ttstreaks: "times tables" }) {
  const since = Date.parse(todayStr) - 7 * 86400000;
  const adaptive = (learning && learning.adaptive) || {};
  const parts = []; let any = false; let mastered = 0; let focus = null;
  for (const [game, st] of Object.entries(adaptive)) {
    if (!st || typeof st !== "object" || !st.sessions) continue;
    const played = (st.sessions || []).filter((x) => x.day && Date.parse(x.day) >= since).length;
    if (played) { any = true; parts.push(`${played} ${gameNames[game] || game} test${played === 1 ? "" : "s"}`); }
    mastered += Object.values(st.topics || {}).filter((t) => t.mastered && t.masteredAt && Date.parse(t.masteredAt) >= since).length;
    if (!focus && Array.isArray(st.practice) && st.practice.length >= 3) focus = { game, count: st.practice.length };
  }
  if (!any) return null;
  const did = parts.join(", ");
  return { name: child.name, did, mastered, focus, text: `${child.name} did ${did}${mastered ? ` and mastered ${mastered} topic${mastered === 1 ? "" : "s"}` : ""}.${focus ? ` Focus area: ${gameNames[focus.game] || focus.game}, ${focus.count} tricky ones coming back.` : ""}` };
}

// ---- Zip B reads: the Adaptive summary / game detail / kid strip ------------------------
// All pure reads over engine state; nothing here writes.

export function testsLastWeek(st, todayStr) {
  const since = Date.parse(todayStr) - 7 * 86400000;
  return ((st && st.log) || []).filter((e) => e.day && Date.parse(e.day) >= since).length;
}

// Last six test scores as percentages, oldest first — the sparkline's points.
export function trendPoints(st) {
  return ((st && st.log) || []).slice(-6).map((e) => (e.total ? Math.round((100 * e.score) / e.total) : 0));
}

export function avgScoreLastMonth(st, todayStr) {
  const since = Date.parse(todayStr) - 30 * 86400000;
  const recent = ((st && st.log) || []).filter((e) => e.day && Date.parse(e.day) >= since && e.total);
  if (!recent.length) return null;
  return Math.round(recent.reduce((a, e) => a + (100 * e.score) / e.total, 0) / recent.length);
}

function masteredSince(st, todayStr, days) {
  const since = Date.parse(todayStr) - days * 86400000;
  return Object.values((st && st.topics) || {}).filter((t) => t.mastered && t.masteredAt && Date.parse(t.masteredAt) >= since).length;
}

// The topic that keeps coming back: most recent-fails, seen enough to mean something.
function stuckTopic(st, params) {
  const P = adaptiveParams(params);
  let worst = null;
  for (const [topic, t] of Object.entries((st && st.topics) || {})) {
    if (t.mastered || !t.recent || t.recent.length < P.M) continue;
    const acc = (100 * t.recent.reduce((a, b) => a + b, 0)) / t.recent.length;
    if (acc <= 40 && (!worst || acc < worst.acc)) worst = { topic, acc };
  }
  return worst && worst.topic;
}

// One of the five agreed states (Stuck, Ready, Drifting, Flying, Practice) or "steady".
// Priority: Drifting (no signal beats stale praise) > Flying > Stuck > Ready > Practice.
// Text is parent-register QUIET; the topic name is the kid-facing label the caller passes.
export function gameInsight(st, todayStr, { topicLabel = (t) => t, params } = {}) {
  if (!st || !(st.log || []).length) return { state: "quiet", text: null };
  const lastDay = st.log[st.log.length - 1].day;
  const quietDays = (Date.parse(todayStr) - Date.parse(lastDay)) / 86400000;
  if (quietDays >= 14) return { state: "drifting", lead: "Drifting.", text: "Not played for two weeks." };
  const flew = masteredSince(st, todayStr, 30);
  if (st.movedUpAt && (Date.parse(todayStr) - Date.parse(st.movedUpAt)) / 86400000 <= 30) return { state: "flying", lead: "Flying.", text: "Moved up this month." };
  if (flew >= 2) return { state: "flying", lead: "Flying.", text: `${flew} topics mastered this month.` };
  const stuck = stuckTopic(st, params);
  if (stuck) return { state: "stuck", lead: `Stuck on ${topicLabel(stuck)}.`, text: "Extra practice is queued." };
  const P = adaptiveParams(params);
  const lastK = (st.sessions || []).slice(-(P.K - 1));
  if (st.target < 5 && lastK.length >= P.K - 1 && lastK.every((x) => x.acc !== null && x.acc >= P.UP && !x.abandoned)) return { state: "ready", lead: "Ready for more.", text: "On track to move up soon." };
  if (Array.isArray(st.practice) && st.practice.length >= 3) return { state: "practice", lead: "A practice week.", text: "Revisiting a few tricky ones." };
  if (flew === 1) return { state: "flying", lead: "Flying.", text: "A topic mastered this month." };
  return { state: "steady", lead: "Ticking along.", text: "Working steadily at this level." };
}

// ---- kid Learn strip (Zip B; Alan's closed boilerplate — screen 5) ----------------------
// One positive line per game, strongest true statement first; nothing is ever invented.
// Titles: the ONE allowed exclamation in the app (decision recorded in DESIGN_GUIDANCE).
export function kidStripLine(game, st, todayStr, { nextLevelLabel } = {}) {
  if (!st) return null;
  const topics = masteredSince(st, todayStr, 30);
  if (st.movedUpAt && (Date.parse(todayStr) - Date.parse(st.movedUpAt)) / 86400000 <= 30) return { kind: "moved", text: "moved up this month" };
  if (topics > 0) return { kind: "topics", text: `${topics} topic${topics === 1 ? "" : "s"} mastered this month` };
  const wordsGames = game === "spelling" || game === "vocab";
  if (wordsGames) {
    const since = Date.parse(todayStr) - 30 * 86400000;
    const words = Object.values(st.topics || {}).reduce((n, t) => n + (t.mastered && t.masteredAt && Date.parse(t.masteredAt) >= since ? t.seen : 0), 0);
    if (words > 0) return { kind: "words", text: `${words} words mastered this month` };
  }
  if (st.target < 5 && (st.sessions || []).slice(-2).every((x) => x.acc !== null && x.acc >= 70) && (st.sessions || []).length >= 2 && nextLevelLabel) return { kind: "nearly", text: `nearly ready for ${nextLevelLabel}` };
  const avg = avgScoreLastMonth(st, todayStr);
  if (avg !== null && avg >= 70) return { kind: "avg", text: `average score ${avg}%` };
  const pts = trendPoints(st);
  if (pts.length >= 4) {
    const half = Math.floor(pts.length / 2);
    const a = pts.slice(0, half).reduce((x, y) => x + y, 0) / half;
    const b = pts.slice(half).reduce((x, y) => x + y, 0) / (pts.length - half);
    const up = Math.round(b - a);
    if (up >= 5) return { kind: "up", text: `score up ${up}%` };
  }
  return null;
}

export function kidStripTitle(lines, anyTrophyThisWeek, quietWeeks) {
  if (quietWeeks) return { key: "welcome", text: "Ready when you are" };
  if (lines.some((l) => l && l.kind === "moved")) return { key: "moving", text: "Moving up!" };
  if (lines.some(Boolean)) return { key: "well", text: "Doing well!" };
  if (anyTrophyThisWeek) return { key: "trophy", text: "New trophy!" };
  return { key: "welcome", text: "Ready when you are" };
}
